import { ParsedIMBStatus } from '@/src/common/constants'
import {
    format12HourTimeWithZone,
    formatFullDateSlashed,
} from '@/src/common/utilities/dateFormatters'
import { styled, theme } from '@/src/stitches.config'
import {
    faClipboardListCheck,
    faPrint,
    faTruck,
} from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const ProgressWrapper = styled('div', {
    margin: '20px auto 10px',
    maxWidth: '90%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
    '&::before': {
        content: '',
        width: 'calc(100% - 40px)',
        height: '3px',
        backgroundColor: '$mediumGray',
        position: 'absolute',
        zIndex: '2',
    },
})

const BlueLine = styled('div', {
    content: '',
    height: '3px',
    backgroundColor: `${theme.colors.secondary}`,
    position: 'absolute',
    zIndex: '5',
})

const StepWrapper = styled('div', {
    zIndex: '10',
    position: 'relative',
    fontSize: '13px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    '@sm': {
        fontSize: '14px',
    },
})

const StepCircle = styled('div', {
    borderRadius: '50%',
    width: '50px',
    height: '50px',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '10',
})

const ProgressBar = ({ progress }: { progress: number }) => {
    return (
        <BlueLine
            style={{
                width: progress === 100 ? 'calc(100% - 40px)' : `${progress}%`,
            }}
        />
    )
}

const ProgressStep = ({ current, completed, icon, label }: any) => {
    if (current) {
        return (
            <StepWrapper>
                <StepCircle
                    style={{
                        border: '2px solid white',
                        boxShadow: '0px 4px 6px 0px rgba(0,0,0,0.3)',
                        background: `-webkit-linear-gradient(83.53deg, ${theme.colors.primary} 18.47%, ${theme.colors.secondary} 78%)`,
                        padding: '1px',
                    }}
                >
                    <FontAwesomeIcon icon={icon} size="xl" />
                </StepCircle>
                <span
                    style={{
                        position: 'absolute',
                        top: '64px',
                        whiteSpace: 'nowrap',
                        color: `${theme.colors.secondary}`,
                    }}
                >
                    {label}
                </span>
            </StepWrapper>
        )
    }

    return (
        <StepWrapper>
            <StepCircle
                style={{
                    backgroundColor: completed
                        ? `${theme.colors.secondary}`
                        : 'white',
                    border: `2px solid ${
                        completed
                            ? theme.colors.secondary
                            : theme.colors.mediumGray
                    }`,
                    color: completed ? 'white' : `${theme.colors.mediumGray}`,
                }}
            >
                <FontAwesomeIcon icon={icon} size="xl" />
            </StepCircle>
            <span
                style={{
                    position: 'absolute',
                    top: '64px',
                    whiteSpace: 'nowrap',
                    color: `${theme.colors.mediumGray}`,
                }}
            >
                {label}
            </span>
        </StepWrapper>
    )
}

export const getDeliveryStatusUpdate = (imbDataEntry: any) => {
    const { status, location, timestamp, expected_delivery_date } = imbDataEntry

    if (status && location && timestamp && expected_delivery_date) {
        const [year, month, day] = expected_delivery_date.split('-')

        const fullDate = `${formatFullDateSlashed(new Date(timestamp))}, 
        ${format12HourTimeWithZone(new Date(timestamp))}`

        return (
            <div style={{ marginBottom: '0.5em' }}>
                Your package was scanned at {location} on {fullDate}. Estimated
                Delivery: {month}/{day}/{year}.
            </div>
        )
    }

    if (status === ParsedIMBStatus.OUT_FOR_DELIVERY) {
        return <span>Out for Delivery</span>
    }

    return <></>
}

export const filterDataByLocation = (imbData: any[]) => {
    const unique: any = {}

    return imbData
        .sort((a, b) =>
            new Date(a.timestamp) < new Date(b.timestamp) ? 1 : -1
        )
        .filter((data) => {
            if (unique[data.location + data.status]) {
                return false
            }

            unique[data.location + data.status] = true
            return true
        })
}

const getProgress = (step: number) => {
    if (step === 1) return 0
    if (step === 2) return 50
    if (step === 3) return 100
    return 0
}

export const DeliveryTracker = ({
    step = 1,
    imbData = null,
    style = {},
}: {
    step: number
    imbData?: any[] | null
    style?: object
}) => {
    return (
        <>
            <ProgressWrapper style={style}>
                <ProgressBar progress={getProgress(step)} />

                <ProgressStep
                    completed={step > 1}
                    current={step == 1}
                    icon={faClipboardListCheck}
                    label="Processing"
                />

                <ProgressStep
                    completed={step > 2}
                    current={step == 2}
                    icon={faPrint}
                    label="Printing"
                />

                <ProgressStep
                    completed={step > 3}
                    current={step >= 3}
                    icon={faTruck}
                    label="Out For Delivery"
                />
            </ProgressWrapper>

            <br />

            {/*only displays in single shipment*/}
            {imbData && (
                <div>
                    <h3>Delivery Updates</h3>
                    <ul
                        style={{
                            padding: '0px 0px 30px 20px',
                            margin: '5px 0px 30px',
                            borderBottom: '2px solid lightgray',
                        }}
                    >
                        {filterDataByLocation(imbData).map((data, i) => {
                            return (
                                <li key={'delivery-update-' + i}>
                                    {getDeliveryStatusUpdate(data)}
                                </li>
                            )
                        })}
                    </ul>
                </div>
            )}
        </>
    )
}
